import { Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Theme } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { Skeleton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { v4 as uuid4 } from 'uuid';
import React from 'react';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    // overflowX lets the skeleton scroll horizontally within its own box when there are more
    // columns than fit, the same way the real DataGridPro contains itself - instead of the
    // table's natural content width pushing this container (and the page layout) wider.
    skeletonWrap: {
      overflowX: 'auto',
      overflowY: 'hidden',
      position: 'relative',
      height: 'calc(100vh - 210px)',
      minHeight: 'calc(100vh - 210px)',
      [theme.breakpoints.down('lg')]: {
        height: 'calc(100vh - 290px)',
        minHeight: 'calc(100vh - 290px)',
      },
      [theme.breakpoints.down('md')]: {
        height: 'calc(100vh - 310px)',
        minHeight: 'calc(100vh - 310px)',
      },
    },
    skeletonTable: {
      width: '100%',
      tableLayout: 'auto',
    },
    skeletonTableHeader: {
      borderTop: `1px solid ${theme.palette.uxGrey.focus}`,
      backgroundColor: theme.palette.uxGrey.hover,
      '& .MuiTableCell-root .MuiButtonBase-root': {
        display: 'none',
      },
    },
    skeletonTableRow: {
      height: 58,
    },
    tableCell: {
      padding: 8,
      fontSize: 16,
      wordWrap: 'break-word',
    },
    headerCell: {
      fontSize: 16,
      height: 54,
      wordWrap: 'break-word',
      fontWeight: 500,
    },
  }),
);

interface SkeletonGridPropTypes {
  hasCheckbox?: boolean;
  headers: GridColDef[];
  rowCount?: number;
}

const SkeletonGrid: React.FC<SkeletonGridPropTypes> = props => {
  const classes = useStyles();
  const { hasCheckbox, headers, rowCount } = props;
  const numberOfRows = rowCount || 10;
  // Only headers that will actually render a cell count toward the width split - dividing by
  // the raw headers.length (which can include hidden columns) understates each cell's share.
  const visibleHeaders = headers.filter(header => !header.hide && (header.headerName || header.renderCell));
  // The checkbox column keeps a fixed, narrow share (matching the real grid's selection column)
  // rather than splitting evenly with data columns, or it ends up roughly double its actual width.
  const checkboxWidthPercent = 3;
  const columnWidthPercent = Math.floor((100 - (hasCheckbox ? checkboxWidthPercent : 0)) / visibleHeaders.length);

  const getCells = (isHeader: boolean) => {
    const cells: JSX.Element[] = [];
    if (hasCheckbox) {
      cells.push(
        <TableCell width={`${checkboxWidthPercent}%`} className={classes.tableCell} key={uuid4()}>
          <Checkbox disabled />
        </TableCell>,
      );
    }
    visibleHeaders.forEach(header => {
      cells.push(
        <TableCell
          key={uuid4()}
          width={`${columnWidthPercent}%`}
          className={isHeader ? classes.headerCell : classes.tableCell}
          align={header.align}
        >
          {isHeader && header.headerName}
          {!isHeader && <Skeleton style={{ float: header.align === 'right' ? 'right' : 'left' }} height="1.4em" width={'85%'} />}
        </TableCell>,
      );
    });
    return cells;
  };

  const rowCells = getCells(false);
  const bodyRows: JSX.Element[] = [];
  for (let i = 1; i < numberOfRows + 1; i += 1) {
    bodyRows.push(
      <TableRow className={classes.skeletonTableRow} key={uuid4()}>
        {rowCells}
      </TableRow>,
    );
  }

  return (
    <TableContainer className={classes.skeletonWrap}>
      <Table className={classes.skeletonTable}>
        <TableHead>
          <TableRow className={classes.skeletonTableHeader}>{getCells(true)}</TableRow>
        </TableHead>
        <TableBody>{bodyRows}</TableBody>
      </Table>
    </TableContainer>
  );
};

export default SkeletonGrid;
